import React, { useState, useEffect } from 'react';
import './App.css';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import UsersManagement from './components/UsersManagement';
import PropertiesManagement from './components/PropertiesManagement';
import TemplatesManagement from './components/TemplatesManagement';
import MyChecklists from './components/MyChecklists';
import TeamChecklists from './components/TeamChecklists';
import DownlineView from './components/DownlineView';
import AdminDashboard from './components/AdminDashboard';
import { api } from './utils/api';
import { 
  LayoutDashboard, 
  CheckSquare, 
  Users, 
  Building2, 
  FileText, 
  Network, 
  BarChart3,
  LogOut,
  Loader2
} from 'lucide-react';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const userData = await api.get('/auth/me');
        setUser(userData);
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const handleLogin = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      localStorage.setItem('token', response.access_token);
      setUser(response.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setCurrentView('dashboard');
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <Loader2 className="spinner-icon" size={48} />
        <p style={{ marginTop: '1rem', color: 'var(--gray-600)' }}>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="app">
      <aside className="sidebar" data-testid="sidebar">
        <div className="sidebar-header">
          <div style={{ textAlign: 'center', width: '100%' }}>
            <img 
              src="/redstone-logo-final.png" 
              alt="Redstone" 
              style={{ 
                height: 'auto',
                width: '100%',
                maxWidth: '180px',
                objectFit: 'contain',
                margin: '0 auto 1.25rem',
                display: 'block'
              }}
            />
            <h1 style={{
              fontFamily: "'Work Sans', sans-serif",
              fontSize: '1.5rem',
              fontWeight: '700',
              color: 'var(--white)',
              letterSpacing: '0.5px',
              margin: '0 0 0.5rem 0',
              lineHeight: 1.2
            }}>
              Redstone Nexus
            </h1>
            <p style={{
              fontSize: '0.8125rem',
              color: 'var(--gray-400)',
              margin: 0,
              fontWeight: '500',
              letterSpacing: '0.3px'
            }}>
              The Center of Ready
            </p>
          </div>
        </div>
        <nav className="sidebar-nav">
          <button
            className={`nav-item ${currentView === 'dashboard' ? 'active' : ''}`}
            onClick={() => setCurrentView('dashboard')}
            data-testid="nav-dashboard"
          >
            <LayoutDashboard size={20} className="nav-icon" />
            <span>Dashboard</span>
          </button>
          <button
            className={`nav-item ${currentView === 'my-checklists' ? 'active' : ''}`}
            onClick={() => setCurrentView('my-checklists')}
            data-testid="nav-my-checklists"
          >
            <CheckSquare size={20} className="nav-icon" />
            <span>My Checklists</span>
          </button>
          {['AREA_MANAGER', 'REGIONAL', 'VP', 'EXEC', 'ADMIN'].includes(user.role) && (
            <>
              <button
                className={`nav-item ${currentView === 'team' ? 'active' : ''}`}
                onClick={() => setCurrentView('team')}
                data-testid="nav-team"
              >
                <Users size={20} className="nav-icon" />
                <span>Team</span>
              </button>
              <button
                className={`nav-item ${currentView === 'downline' ? 'active' : ''}`}
                onClick={() => setCurrentView('downline')}
                data-testid="nav-downline"
              >
                <Network size={20} className="nav-icon" />
                <span>Downline</span>
              </button>
            </>
          )}
          {user.role === 'ADMIN' && (
            <>
              <div className="nav-divider"></div>
              <button
                className={`nav-item ${currentView === 'admin-dashboard' ? 'active' : ''}`}
                onClick={() => setCurrentView('admin-dashboard')}
                data-testid="nav-admin-dashboard"
              >
                <BarChart3 size={20} className="nav-icon" />
                <span>Analytics</span>
              </button>
              <button
                className={`nav-item ${currentView === 'users' ? 'active' : ''}`}
                onClick={() => setCurrentView('users')}
                data-testid="nav-users"
              >
                <Users size={20} className="nav-icon" />
                <span>Users</span>
              </button>
              <button
                className={`nav-item ${currentView === 'properties' ? 'active' : ''}`}
                onClick={() => setCurrentView('properties')}
                data-testid="nav-properties"
              >
                <Building2 size={20} className="nav-icon" />
                <span>Properties</span>
              </button>
              <button
                className={`nav-item ${currentView === 'templates' ? 'active' : ''}`}
                onClick={() => setCurrentView('templates')}
                data-testid="nav-templates"
              >
                <FileText size={20} className="nav-icon" />
                <span>Templates</span>
              </button>
            </>
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{user.name.charAt(0)}</div>
            <div className="user-details">
              <div className="user-name">{user.name}</div>
              <div className="user-role">{user.role.replace('_', ' ')}</div>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout} data-testid="logout-btn">
            <LogOut size={18} className="logout-icon" />
            <span>Logout</span>
          </button>
        </div>
      </aside>
      <main className="main-content">
        {currentView === 'dashboard' && <Dashboard user={user} />}
        {currentView === 'my-checklists' && <MyChecklists user={user} />}
        {currentView === 'team' && <TeamChecklists user={user} />}
        {currentView === 'downline' && <DownlineView user={user} />}
        {currentView === 'admin-dashboard' && <AdminDashboard user={user} />}
        {currentView === 'users' && <UsersManagement user={user} />}
        {currentView === 'properties' && <PropertiesManagement user={user} />}
        {currentView === 'templates' && <TemplatesManagement user={user} />}
      </main>
    </div>
  );
}

export default App;
